import { motion } from "framer-motion";
import { Plane, MapPin, Clock, Car, CheckCircle2, Phone } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import SectionHeading from "@/components/SectionHeading";
import BookingForm from "@/components/BookingForm";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const services = [
  {
    icon: MapPin,
    title: "Trichy to Velankanni Special",
    desc: "Our premium pilgrimage specialized service. We offer dedicated taxi packages for families and groups traveling to the Basilica of Our Lady of Good Health in Velankanni. Comfortable, safe, and punctual.",
    features: ["Pilgrimage Experts", "Direct Route", "Family Friendly", "Safe Night Travel", "Clean Vehicles", "Reliable Support"],
    special: true,
  },
  {
    icon: MapPin,
    title: "Local Trichy Sightseeing",
    desc: "Experience the best taxi services in Trichy with our curated sightseeing tours. Visit the Rockfort, Srirangam, and Kallanai Dam with our knowledgeable drivers who act as your local guides.",
    features: ["Rockfort Temple", "Srirangam Visit", "Kallanai Dam", "Temple Tours", "Custom Itineraries", "Half/Full Day"],
  },
  {
    icon: Plane,
    title: "Airport pickup / Airport drop",
    desc: "Reliable pickup and drop services for Trichy International Airport (TRZ). We ensure timely arrivals and departures with professional meet-and-greet services.",
    features: ["Trichy Airport", "Tanjore/Madurai Connectivity", "Flight Tracking", "Meet & Greet", "24/7 Availability", "Luggage Support"],
  },
  {
    icon: Car,
    title: "Outstation Trips",
    desc: "Comfortable long-distance travel from Trichy to anywhere in South India. Popular destinations include Madurai, Tanjore, Kumbakonam, and more with experienced highway drivers.",
    features: ["Round Trips", "Multi-day Tours", "Experienced Drivers", "Interstate Permits", "All-India Service", "Flexible Returns"],
  },
  {
    icon: Clock,
    title: "Pick up and Drop Services",
    desc: "Convenient point-to-point transfers within and around Trichy. Whether it's a railway station transfer or a ride to your destination, we've got you covered.",
    features: ["Railway Station", "Bus Stand", "Hotel Transfers", "Quick Bookings", "Safe & Secure", "Doorstep Pickup"],
  },
  {
    icon: Car,
    title: "Corporate Cab Service",
    desc: "Professional transportation solutions for businesses and organizations in Trichy. We provide reliable fleet management for employee commutes and client pickups.",
    features: ["Monthly Billing", "Priority Booking", "High-End Fleet", "Professional Drivers", "Event Transport", "Corporate Rates"],
  },
];

const Services = () => (
  <Layout>
    <SEOHead
      title="Taxi Services in Trichy | Local, Airport & Outstation Cabs"
      description="Best taxi services in Trichy for local sightseeing, Airport pickup / Airport drop and outstation trips. Book affordable and reliable cab service in Trichy today."
      keywords="taxi services in trichy, trichy taxi, cab booking trichy, trichy to velankanni taxi, outstation taxi trichy, cheap taxi trichy"
    />

    {/* Hero */}
    <section className="pt-28 pb-16 section-padding bg-gradient-to-b from-primary/5 to-transparent">
      <div className="container-custom text-center">
        <motion.h1 {...fadeUp} className="text-4xl sm:text-5xl font-bold">
          Our <span className="text-gradient">Taxi Services</span> in Trichy
        </motion.h1>
        <motion.p {...fadeUp} className="mt-4 text-muted-foreground max-w-2xl mx-auto">
          From local rides around Trichy to long-distance outstation travel — we offer the best taxi services in Trichy tailored to every traveler's needs.
        </motion.p>
      </div>
    </section>

    {/* Services */}
    <section className="section-padding">
      <div className="container-custom space-y-16">
        {services.map((s, i) => (
          <motion.div
            key={s.title}
            {...fadeUp}
            transition={{ delay: 0.1 }}
            className={`grid md:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? "md:direction-rtl" : ""} ${s.special ? "p-8 rounded-[2.5rem] bg-primary/5 border border-primary/20 shadow-2xl relative overflow-hidden" : ""}`}
          >
            {s.special && (
              <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-6 py-1 rounded-bl-2xl font-bold text-xs tracking-widest">
                RECOMMENDED
              </div>
            )}
            <div className={i % 2 === 1 ? "md:order-2" : ""}>
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-5 ${s.special ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20" : "bg-primary/10 text-primary"}`}>
                <s.icon className="w-7 h-7" />
              </div>
              <h2 className={`text-2xl font-bold mb-4 ${s.special ? "text-primary" : "text-foreground"}`}>{s.title}</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">{s.desc}</p>
              <div className="grid grid-cols-2 gap-3">
                {s.features.map((f) => (
                  <span key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${s.special ? "text-primary" : "text-primary"}`} /> {f}
                  </span>
                ))}
              </div>
              <a href="tel:+917418749974" className={`btn-primary inline-flex items-center gap-2 mt-6 ${s.special ? "px-8 py-3 shadow-xl" : ""}`}>
                <Phone className="w-4 h-4" /> Book {s.title}
              </a>
            </div>
            <div className={`glass-card rounded-2xl p-10 flex items-center justify-center min-h-[300px] ${i % 2 === 1 ? "md:order-1" : ""} ${s.special ? "bg-white/5 border-white/10" : ""}`}>
              {s.special ? (
                <img src="/velankanni_hero.png" alt="Velankanni Taxi" className="w-full h-full object-cover rounded-xl" />
              ) : (
                <s.icon className="w-24 h-24 text-primary/20" />
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </section>

    {/* CTA */}
    <section className="section-padding bg-taxi-darker/50">
      <div className="container-custom max-w-2xl">
        <SectionHeading badge="Book Now" title="Ready to" highlight="Ride?" description="Fill in the form below and we'll confirm your taxi booking in Trichy instantly." />
        <BookingForm idPrefix="services" />
      </div>
    </section>
  </Layout>
);

export default Services;
